import React from 'react';
import { motion } from 'framer-motion';
import { CONSTANTS } from '../data.js';

type AudioSettingsPopupProps = {
  onClose: () => void;
  musicEnabled: boolean;
  onToggleMusic: () => void;
  musicVolume: number;
  onMusicVolumeChange: (volume: number) => void;
  sfxEnabled: boolean;
  onToggleSfx: () => void;
  sfxVolume: number;
  onSfxVolumeChange: (volume: number) => void;
};

export const AudioSettingsPopup: React.FC<AudioSettingsPopupProps> = ({
  onClose,
  musicEnabled,
  onToggleMusic,
  musicVolume,
  onMusicVolumeChange,
  sfxEnabled,
  onToggleSfx,
  sfxVolume,
  onSfxVolumeChange,
}) => {
  return (
    <motion.div
      className="absolute inset-0 flex items-center justify-center z-50"
      style={{ background: 'rgba(26, 22, 17, 0.85)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="relative flex flex-col p-8 w-[450px] max-w-[90vw] rounded-xl"
        style={{
          background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface} 0%, ${CONSTANTS.ui.theme.colors.background} 100%)`,
          border: `2px solid ${CONSTANTS.ui.theme.colors.border}`,
          boxShadow: CONSTANTS.ui.theme.effects.ornateBoxShadow
        }}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* Decorative corners */}
        <div className="absolute top-3 left-3 w-6 h-6 border-l-2 border-t-2 rounded-tl-lg" style={{ borderColor: CONSTANTS.ui.theme.colors.primary }} />
        <div className="absolute top-3 right-3 w-6 h-6 border-r-2 border-t-2 rounded-tr-lg" style={{ borderColor: CONSTANTS.ui.theme.colors.primary }} />
        <div className="absolute bottom-3 left-3 w-6 h-6 border-l-2 border-b-2 rounded-bl-lg" style={{ borderColor: CONSTANTS.ui.theme.colors.primary }} />
        <div className="absolute bottom-3 right-3 w-6 h-6 border-r-2 border-b-2 rounded-br-lg" style={{ borderColor: CONSTANTS.ui.theme.colors.primary }} />

        <h2 
          className="font-cinzel text-3xl font-bold mb-8 text-center"
          style={{ 
            color: CONSTANTS.ui.theme.colors.primary,
            textShadow: CONSTANTS.ui.theme.effects.textGlow
          }}
        >
          Audio Settings
        </h2>

        <div className="space-y-8">
          {/* Music Settings */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label 
                htmlFor="music-toggle" 
                className="font-crimson text-xl font-semibold"
                style={{ color: CONSTANTS.ui.theme.colors.text }}
              >
                Music
              </label>
              <button
                id="music-toggle"
                onClick={onToggleMusic}
                className="font-crimson px-6 py-2 rounded-lg text-sm font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
                style={{
                  background: musicEnabled 
                    ? `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.success} 0%, #32cd32 100%)`
                    : `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface} 0%, #3d3426 100%)`,
                  color: musicEnabled ? CONSTANTS.ui.theme.colors.background : CONSTANTS.ui.theme.colors.textSecondary,
                  border: `1px solid ${musicEnabled ? CONSTANTS.ui.theme.colors.success : CONSTANTS.ui.theme.colors.border}`,
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                }}
              >
                {musicEnabled ? 'Enabled' : 'Disabled'}
              </button>
            </div>
            <div className="flex items-center gap-4">
              <span 
                className="font-crimson text-sm"
                style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
              >
                Volume
              </span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={musicVolume}
                onChange={(e) => onMusicVolumeChange(parseFloat(e.target.value))}
                className="flex-1 h-2 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(90deg, ${CONSTANTS.ui.theme.colors.surface} 0%, ${CONSTANTS.ui.theme.colors.primary} 100%)`,
                  accentColor: CONSTANTS.ui.theme.colors.primary
                }}
                disabled={!musicEnabled}
              />
            </div>
          </div>

          {/* SFX Settings */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label 
                htmlFor="sfx-toggle" 
                className="font-crimson text-xl font-semibold"
                style={{ color: CONSTANTS.ui.theme.colors.text }}
              >
                Sound Effects
              </label>
              <button
                id="sfx-toggle"
                onClick={onToggleSfx}
                className="font-crimson px-6 py-2 rounded-lg text-sm font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
                style={{
                  background: sfxEnabled 
                    ? `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.accent} 0%, #daa520 100%)`
                    : `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface} 0%, #3d3426 100%)`,
                  color: sfxEnabled ? CONSTANTS.ui.theme.colors.background : CONSTANTS.ui.theme.colors.textSecondary,
                  border: `1px solid ${sfxEnabled ? CONSTANTS.ui.theme.colors.accent : CONSTANTS.ui.theme.colors.border}`,
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                }}
              >
                {sfxEnabled ? 'Enabled' : 'Disabled'}
              </button>
            </div>
            <div className="flex items-center gap-4">
              <span 
                className="font-crimson text-sm"
                style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
              >
                Volume
              </span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={sfxVolume}
                onChange={(e) => onSfxVolumeChange(parseFloat(e.target.value))}
                className="flex-1 h-2 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(90deg, ${CONSTANTS.ui.theme.colors.surface} 0%, ${CONSTANTS.ui.theme.colors.accent} 100%)`,
                  accentColor: CONSTANTS.ui.theme.colors.accent
                }}
                disabled={!sfxEnabled}
              />
            </div>
          </div>
        </div>

        <button
          onClick={onClose}
          className="font-crimson mt-10 w-full px-8 py-4 rounded-lg text-xl font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
          style={{
            background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary} 0%, ${CONSTANTS.ui.theme.colors.accent} 100%)`,
            color: CONSTANTS.ui.theme.colors.background,
            border: `2px solid ${CONSTANTS.ui.theme.colors.border}`,
            boxShadow: CONSTANTS.ui.theme.effects.ornateBoxShadow
          }}
        >
          Return to Quest
        </button>
      </motion.div>
    </motion.div>
  );
};
